import React, { useCallback, useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import toast from 'react-hot-toast'
import orderService from '../services/orderService'
import AddOrderItemModal from '../components/AddOrderItemModal'
import {
  RestaurantOrder,
  RestaurantOrderStatus,
  ORDER_STATUS_LABELS,
  ORDER_STATUS_BADGE,
} from '../types/order'
import { useTranslation } from '../hooks/use-i18n'

const OrderDetail: React.FC = () => {
  const { t } = useTranslation()
  const { restaurantId, orderId } = useParams<{ restaurantId: string; orderId: string }>()
  const navigate = useNavigate()
  const [order, setOrder] = useState<RestaurantOrder | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [showAddModal, setShowAddModal] = useState(false)
  const [busy, setBusy] = useState(false)
  const [tableLabel, setTableLabel] = useState('')
  const [note, setNote] = useState('')

  const load = useCallback(async () => {
    if (!restaurantId || !orderId) return
    try {
      setIsLoading(true)
      const data = await orderService.getOrder(restaurantId, orderId)
      setOrder(data)
      setTableLabel(data.tableLabel || '')
      setNote(data.note || '')
    } catch (err: any) {
      toast.error(err.response?.data?.message || t('error.failed'))
    } finally {
      setIsLoading(false)
    }
  }, [restaurantId, orderId, t])

  useEffect(() => {
    load()
  }, [load])

  const isOpen = order?.status === 'open'

  const changeQty = async (itemId: string, quantity: number) => {
    if (!restaurantId || !orderId || quantity < 1) return
    try {
      const updated = await orderService.updateItem(restaurantId, orderId, itemId, quantity)
      setOrder(updated)
    } catch (err: any) {
      toast.error(err.response?.data?.message || t('toast.updateItemFailed'))
    }
  }

  const removeItem = async (itemId: string) => {
    if (!restaurantId || !orderId) return
    try {
      const updated = await orderService.removeItem(restaurantId, orderId, itemId)
      setOrder(updated)
    } catch (err: any) {
      toast.error(err.response?.data?.message || t('toast.removeItemFailed'))
    }
  }

  const saveDetails = async () => {
    if (!restaurantId || !orderId) return
    try {
      setBusy(true)
      const updated = await orderService.updateOrder(restaurantId, orderId, {
        tableLabel,
        note,
      })
      setOrder(updated)
      toast.success(t('toast.orderUpdated'))
    } catch (err: any) {
      toast.error(err.response?.data?.message || t('toast.orderUpdateFailed'))
    } finally {
      setBusy(false)
    }
  }

  const setStatus = async (status: RestaurantOrderStatus) => {
    if (!restaurantId || !orderId) return
    try {
      setBusy(true)
      const updated = await orderService.updateOrder(restaurantId, orderId, { status })
      setOrder(updated)
      toast.success(t('toast.orderStatusUpdated', { status: ORDER_STATUS_LABELS[status].toLowerCase() }))
    } catch (err: any) {
      toast.error(err.response?.data?.message || t('toast.orderStatusUpdateFailed'))
    } finally {
      setBusy(false)
    }
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sky-500"></div>
      </div>
    )
  }

  if (!order) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="bg-[#2a2a2a] border border-white/10 rounded-3xl p-8 max-w-md w-full text-center">
          <h2 className="text-2xl font-bold text-white mb-4">{t('order.detail.notFound.title')}</h2>
          <button
            onClick={() => navigate(`/restaurant/${restaurantId}/orders`)}
            className="inline-block bg-black hover:bg-black/80 text-white px-6 py-3 rounded-full font-semibold transition-all"
          >
            {t('order.detail.notFound.back')}
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen">
      <main className="max-w-3xl mx-auto px-3 sm:px-4 lg:px-8 py-4 sm:py-6 lg:py-8">
        <div className="mb-6">
          <Link
            to={`/restaurant/${restaurantId}/orders`}
            className="inline-flex items-center text-white/80 hover:text-white mb-4 transition-colors"
          >
            {t('common.backToOrders')}
          </Link>
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-2xl sm:text-3xl font-bold text-white">
              {order.tableLabel ? t('order.list.table', { label: order.tableLabel }) : t('order.fallbackLabel')}
            </h1>
            <span
              className={`px-3 py-1 rounded-full text-xs font-medium ${ORDER_STATUS_BADGE[order.status]}`}
            >
              {ORDER_STATUS_LABELS[order.status]}
            </span>
          </div>
          <p className="text-white/50 text-sm mt-1">
            {t('order.detail.takenBy', { name: order.createdBy?.name || t('common.fallbackStaff'), date: new Date(order.createdAt).toLocaleString() })}
          </p>
        </div>

        {/* Order details (editable while open) */}
        <div className="bg-[#2a2a2a] border border-white/10 rounded-2xl p-5 mb-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-white/80 mb-2">{t('order.detail.tableLabel')}</label>
              <input
                type="text"
                value={tableLabel}
                onChange={(e) => setTableLabel(e.target.value)}
                disabled={!isOpen}
                placeholder={t('order.detail.tablePlaceholder')}
                className="w-full px-4 py-2.5 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-sky-500 disabled:opacity-60"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-white/80 mb-2">{t('order.detail.noteLabel')}</label>
              <input
                type="text"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                disabled={!isOpen}
                placeholder={t('order.detail.notePlaceholder')}
                className="w-full px-4 py-2.5 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-sky-500 disabled:opacity-60"
              />
            </div>
          </div>
          {isOpen && (
            <button
              type="button"
              onClick={saveDetails}
              disabled={busy}
              className="mt-3 text-sky-400 hover:text-sky-300 text-sm font-medium disabled:opacity-50"
            >
              {t('order.detail.saveDetails')}
            </button>
          )}
        </div>

        {/* Items */}
        <div className="bg-[#2a2a2a] border border-white/10 rounded-2xl p-5 mb-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-white">{t('order.detail.itemsTitle')}</h2>
            {isOpen && (
              <button
                type="button"
                onClick={() => setShowAddModal(true)}
                className="px-4 py-2 bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-600 hover:to-blue-700 text-white rounded-lg text-sm font-semibold transition-all"
              >
                {t('order.detail.addItems')}
              </button>
            )}
          </div>

          {order.items.length === 0 ? (
            <p className="text-white/50 text-sm py-6 text-center">{t('order.detail.noItems')}</p>
          ) : (
            <div className="space-y-2">
              {order.items.map((item) => (
                <div
                  key={item._id}
                  className="bg-[#1f1f1f] rounded-xl p-3 flex items-center justify-between gap-3 flex-wrap"
                >
                  <div className="min-w-0">
                    <p className="text-white font-medium truncate">{item.name}</p>
                    <p className="text-white/50 text-xs">{t('order.detail.unitPriceEach', { price: item.unitPrice.toFixed(2) })}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    {isOpen ? (
                      <div className="flex items-center bg-white/10 border border-white/20 rounded-lg">
                        <button
                          type="button"
                          onClick={() => changeQty(item._id, item.quantity - 1)}
                          className="px-2.5 py-1.5 text-white/80 hover:text-white"
                        >
                          −
                        </button>
                        <span className="w-8 text-center text-white text-sm">{item.quantity}</span>
                        <button
                          type="button"
                          onClick={() => changeQty(item._id, item.quantity + 1)}
                          className="px-2.5 py-1.5 text-white/80 hover:text-white"
                        >
                          +
                        </button>
                      </div>
                    ) : (
                      <span className="text-white/70 text-sm">×{item.quantity}</span>
                    )}
                    <span className="text-white font-semibold w-16 text-right">
                      ${item.lineTotal.toFixed(2)}
                    </span>
                    {isOpen && (
                      <button
                        type="button"
                        onClick={() => removeItem(item._id)}
                        className="text-red-400 hover:text-red-300 text-sm"
                      >
                        ✕
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="flex items-center justify-between mt-4 pt-4 border-t border-white/10">
            <span className="text-white/70 font-medium">{t('order.detail.total')}</span>
            <span className="text-white font-bold text-xl">${order.total.toFixed(2)}</span>
          </div>
        </div>

        {/* Status actions */}
        <div className="flex gap-3 flex-wrap">
          {order.status === 'open' && (
            <>
              <button
                type="button"
                onClick={() => setStatus('submitted')}
                disabled={busy || order.items.length === 0}
                className="flex-1 min-w-[140px] px-5 py-3 bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-600 hover:to-blue-700 text-white rounded-xl font-semibold transition-all disabled:opacity-50"
              >
                {t('order.detail.submit')}
              </button>
              <button
                type="button"
                onClick={() => setStatus('cancelled')}
                disabled={busy}
                className="px-5 py-3 border-2 border-red-500/40 text-red-300 rounded-xl font-semibold hover:bg-red-500/10 transition-all disabled:opacity-50"
              >
                {t('order.detail.cancel')}
              </button>
            </>
          )}
          {order.status === 'submitted' && (
            <>
              <button
                type="button"
                onClick={() => setStatus('served')}
                disabled={busy}
                className="flex-1 min-w-[140px] px-5 py-3 bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700 text-white rounded-xl font-semibold transition-all disabled:opacity-50"
              >
                {t('order.detail.markServed')}
              </button>
              <button
                type="button"
                onClick={() => setStatus('cancelled')}
                disabled={busy}
                className="px-5 py-3 border-2 border-red-500/40 text-red-300 rounded-xl font-semibold hover:bg-red-500/10 transition-all disabled:opacity-50"
              >
                {t('order.detail.cancel')}
              </button>
            </>
          )}
        </div>

        {showAddModal && restaurantId && orderId && (
          <AddOrderItemModal
            restaurantId={restaurantId}
            orderId={orderId}
            onClose={() => setShowAddModal(false)}
            onItemAdded={load}
          />
        )}
      </main>
    </div>
  )
}

export default OrderDetail
